import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Skeleton,
  type SxProps,
  type Theme,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VideocamIcon from '@mui/icons-material/Videocam';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import type { DashboardData } from '../../types/canvision.types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface StatsCardsProps {
  /** Dashboard stats from GET /dashboard (null while loading) */
  stats: DashboardData | null;
  /** Whether the data is currently loading */
  loading?: boolean;
  sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// Internal stat card
// ---------------------------------------------------------------------------

interface StatCardData {
  label: string;
  icon: React.ReactNode;
  primary: number;
  breakdown: string;
  borderColor: string;
}

function StatCard({ label, icon, primary, breakdown, borderColor }: StatCardData) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: `4px solid ${borderColor}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={{ color: borderColor, display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
      </Box>

      <Typography variant="h4" fontWeight={700}>
        {primary}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        {breakdown}
      </Typography>
    </Paper>
  );
}

function StatCardSkeleton() {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '4px solid',
        borderLeftColor: 'action.disabledBackground',
      }}
    >
      <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="30%" height={40} />
      <Skeleton variant="text" width="70%" height={16} />
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * 2x2 grid of stat cards showing Sites, Cameras, Events, and Incidents.
 *
 * Each card follows the CANsense pattern: Paper with a colored left border,
 * a primary count (h4), and a secondary breakdown line.
 *
 * Data is sourced from `DashboardData` (GET /dashboard, polled every 30 s).
 */
export function StatsCards({ stats, loading = false, sx }: StatsCardsProps) {
  const theme = useTheme();

  // -- Loading state --------------------------------------------------------
  if (loading || !stats) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          ...sx,
        }}
      >
        <Grid container spacing={2}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={6} key={i}>
              <StatCardSkeleton />
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }

  // -- Build card data from stats -------------------------------------------
  const cards: StatCardData[] = [
    {
      label: 'Sites',
      icon: <LocationOnIcon fontSize="small" />,
      primary: stats.sites.total,
      breakdown: `${stats.sites.up} up / ${stats.sites.down} down`,
      borderColor: theme.palette.primary.main,
    },
    {
      label: 'Cameras',
      icon: <VideocamIcon fontSize="small" />,
      primary: stats.cameras.total,
      breakdown: `${stats.cameras.enabled} online / ${stats.cameras.total - stats.cameras.enabled} offline`,
      borderColor: theme.palette.info.main,
    },
    {
      label: 'Events',
      icon: <NotificationsActiveIcon fontSize="small" />,
      primary: stats.recent_events.new,
      breakdown: `${stats.recent_events.acknowledged} ack / ${stats.recent_events.total - stats.recent_events.new - stats.recent_events.acknowledged} dismissed`,
      borderColor: theme.palette.warning.main,
    },
    {
      label: 'Incidents',
      icon: <ReportProblemIcon fontSize="small" />,
      primary: stats.incidents?.active ?? 0,
      breakdown: `${stats.incidents?.new ?? 0} new / ${stats.incidents?.reviewing ?? 0} reviewing / ${stats.incidents?.dispatched ?? 0} dispatched`,
      borderColor: theme.palette.error.main,
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        ...sx,
      }}
    >
      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={6} key={card.label}>
            <StatCard {...card} />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

export default StatsCards;
